import { braveWallet } from "@rainbow-me/rainbowkit/wallets";
import { createConnector } from "wagmi";
import { injected } from "wagmi/connectors";

import { chosenOnly, keepPermissions } from "@/lib/wallet/evm-chosen";
import { injectedProviders } from "@/lib/wallet/evm-gated-entry";

import type { Wallet, WalletDetailsParams } from "@rainbow-me/rainbowkit";
import type { EIP1193Provider } from "viem";

export const BRAVE_EVM_CHOSEN = "mudwater.brave-evm.chosen";

/** Prefer Brave's dedicated namespace even when another extension is default. */
export function injectedBrave(): EIP1193Provider | undefined {
  if (typeof window === "undefined") return undefined;
  const candidates = [
    Reflect.get(window, "braveEthereum"),
    ...injectedProviders(Reflect.get(window, "ethereum")),
  ];
  return candidates.find(
    (provider): provider is EIP1193Provider =>
      typeof provider === "object" &&
      provider !== null &&
      Reflect.get(provider, "isBraveWallet") === true &&
      Reflect.get(provider, "isPhantom") !== true &&
      Reflect.get(provider, "isRabby") !== true &&
      Reflect.get(provider, "isPrivyWallet") !== true &&
      typeof Reflect.get(provider, "request") === "function" &&
      typeof Reflect.get(provider, "on") === "function" &&
      typeof Reflect.get(provider, "removeListener") === "function"
  );
}

/** A named, explicit choice. EVM logout must not revoke the Solana permission. */
export function braveEntry(): Wallet {
  return {
    ...braveWallet(),
    installed: Boolean(injectedBrave()),
    createConnector: (details: WalletDetailsParams) =>
      createConnector((config) => {
        let resolved: EIP1193Provider | undefined;
        const base = injected({
          target: () => ({
            id: "brave",
            name: "Brave Wallet",
            provider: () => (resolved ??= injectedBrave()),
          }),
        })(config);
        return {
          ...chosenOnly(BRAVE_EVM_CHOSEN, base),
          ...details,
          async getProvider() {
            const provider = await base.getProvider.call(this);
            return provider ? keepPermissions(provider) : provider;
          },
        };
      }),
  };
}
